import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import soundManager from '../utils/SoundManager';
import { Howler } from 'howler';

const SoundContext = createContext();

export const useSoundContext = () => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSoundContext must be used within a SoundProvider');
  }
  return context;
};

const STORAGE_KEY = 'crash-game-sound-settings';

export const SoundProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [volumes, setVolumes] = useState({
    master: 0.5,
    sfx: 0.5,
    music: 0.5,
  });
  const [muted, setMuted] = useState(false);

  // Load settings from localStorage
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);

        // Apply saved volumes
        if (settings.volumes) {
          setVolumes(settings.volumes);
          soundManager.setMasterVolume(settings.volumes.master);
          soundManager.setSFXVolume(settings.volumes.sfx);
          soundManager.setMusicVolume(settings.volumes.music);
        }

        // Apply saved mute state
        if (settings.muted !== undefined) {
          setMuted(settings.muted);
          soundManager.toggleMute(settings.muted);
        }
      }
    } catch (error) {
      console.error('Error loading sound settings:', error);
    }
  }, []);

  // Initialize SoundManager on mount
  useEffect(() => {
    if (!initialized) {
      soundManager.init();
      setInitialized(true);
    }

    // Cleanup on unmount
    return () => {
      soundManager.cleanup();
    };
  }, [initialized]);

  // Unlock audio on first user interaction (browser autoplay policy)
  useEffect(() => {
    if (initialized && !audioUnlocked) {
      const unlockAudio = () => {
        soundManager.unlockAudio();
        setAudioUnlocked(true);
        // Remove listeners after first unlock
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('touchstart', unlockAudio);
        document.removeEventListener('keydown', unlockAudio);
      };

      // Listen for any user interaction
      document.addEventListener('click', unlockAudio);
      document.addEventListener('touchstart', unlockAudio);
      document.addEventListener('keydown', unlockAudio);

      return () => {
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('touchstart', unlockAudio);
        document.removeEventListener('keydown', unlockAudio);
      };
    }
  }, [initialized, audioUnlocked]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (initialized) {
      try {
        const settings = {
          volumes,
          muted,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      } catch (error) {
        console.error('Error saving sound settings:', error);
      }
    }
  }, [volumes, muted, initialized]);

  // Handle mobile audio context suspension when app goes to background
  useEffect(() => {
    if (!initialized || !audioUnlocked) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Resume audio context when page becomes visible
        if (Howler.ctx && Howler.ctx.state === 'suspended') {
          Howler.ctx.resume().then(() => {
            console.log('Audio context resumed after visibility change');
          }).catch(err => {
            console.error('Error resuming audio context:', err);
          });
        }
      }
    };

    // Listen for visibility changes (app switching, home screen, etc.)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also listen for page focus events as a fallback
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [initialized, audioUnlocked]);

  // Play a sound effect
  const playSoundEffect = useCallback((soundName) => {
    if (initialized && !muted) {
      soundManager.playSoundEffect(soundName);
    }
  }, [initialized, muted]);

  // Play background music
  const playBackgroundMusic = useCallback((loop = true, fadeDuration = 500) => {
    if (initialized && !muted) {
      soundManager.playBackgroundMusic(loop, fadeDuration);
    }
  }, [initialized, muted]);

  // Stop background music
  const stopBackgroundMusic = useCallback((fadeDuration = 500) => {
    if (initialized) {
      soundManager.stopBackgroundMusic(fadeDuration);
    }
  }, [initialized]);

  // Set master volume
  const setMasterVolume = useCallback((volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    setVolumes(prev => ({ ...prev, master: clampedVolume }));
    soundManager.setMasterVolume(clampedVolume);
  }, []);

  // Set SFX volume
  const setSFXVolume = useCallback((volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    setVolumes(prev => ({ ...prev, sfx: clampedVolume }));
    soundManager.setSFXVolume(clampedVolume);
  }, []);

  // Set music volume
  const setMusicVolume = useCallback((volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    setVolumes(prev => ({ ...prev, music: clampedVolume }));
    soundManager.setMusicVolume(clampedVolume);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const newMuted = !muted;
    setMuted(newMuted);
    soundManager.toggleMute(newMuted);
  }, [muted]);

  const value = {
    initialized,
    audioUnlocked,
    volumes,
    muted,
    playSoundEffect,
    playBackgroundMusic,
    stopBackgroundMusic,
    setMasterVolume,
    setSFXVolume,
    setMusicVolume,
    toggleMute,
  };

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
};
